// R116 #5 (Jorge: "upload videos as before or after evidence… some issues or
// resolutions are impossible to show in a picture") — ONE media strip for
// every evidence surface (work orders, the calendar WO inspector, the Issues
// board, quick-logs, claims). It replaces the <img>-only grids that drew a
// video url as a broken image, and the "🎥 Video 3" text links.
//
//   mediaStrip(items, opts)  → HTML: square tiles; a photo is an <img>, a video
//                              is a muted first-frame poster (`#t=0.1` makes
//                              iOS paint one) with a ▶ badge. Tiles are
//                              <button>s that open the shared Lightbox on the
//                              whole strip (‹ › through photos AND clips).
//   bindMediaStrips(root)    → one delegated listener per root (idempotent):
//                              tap → Lightbox; and the HEVC fallback — a clip
//                              this browser can't decode (an iPhone .mov on an
//                              older PC) fires `error`, or loads metadata with
//                              videoWidth 0 (audio-only playback); either way
//                              the tile becomes a clear "🎥 Video" card that
//                              still opens (the Lightbox offers "open
//                              original" to download it).
//   pendingTilesHtml(items)  → tiles for files still in the upload queue,
//                              with a progress bar (patched live by
//                              patchPendingProgress on 'surge-photo-progress').
//
// Styles are injected once (prefixed mx-, tokens only — they follow the theme).
import { openLightbox } from '../components/Lightbox';
import { isVideoUrl, fmtBytes } from './mediaKind';
import type { PendingItem } from '../../lib/photoUploader';

export { isVideoUrl, isVideoFile, isMediaFile, EVIDENCE_ACCEPT, pickSummary, fmtBytes } from './mediaKind';

export interface MediaItem {
  url: string;
  /** Force video (the record said so); otherwise isVideoUrl decides. */
  video?: boolean;
  /** Lightbox caption (plain text — escaped here). */
  caption?: string;
}
export interface StripOpts {
  /** Tile size: sm 52px · md 72px (default) · lg 96px. */
  size?: 'sm' | 'md' | 'lg';
  /** Show at most this many tiles, then a "+N" tile (the Lightbox still pages through all). */
  max?: number;
  /** Extra classes on the strip. */
  cls?: string;
  /** Alt text stem for photos ("Before photo 2 of 5"). */
  alt?: string;
  /** Extra tiles appended inside the same strip (e.g. pendingTilesHtml). */
  append?: string;
}

const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

/** Urls (+ the record's own video list, which wins over the extension) → strip items. */
export function toMediaItems(urls: unknown, videos?: unknown, caption?: (url: string, video: boolean) => string): MediaItem[] {
  const vids = new Set<string>((Array.isArray(videos) ? videos : []).filter((v: any) => typeof v === 'string'));
  const out: MediaItem[] = [];
  const seen = new Set<string>();
  for (const u of Array.isArray(urls) ? urls : []) {
    if (typeof u !== 'string' || !u || seen.has(u)) continue;
    seen.add(u);
    const video = vids.has(u) || isVideoUrl(u);
    out.push({ url: u, video, caption: caption ? caption(u, video) : undefined });
  }
  return out;
}

function tile(m: MediaItem, i: number, n: number, opts: StripOpts, hidden: boolean): string {
  const video = m.video === true || (m.video !== false && isVideoUrl(m.url));
  const cap = m.caption ? ` data-mx-cap="${esc(m.caption)}"` : '';
  const hid = hidden ? ' mx-hide' : '';
  if (video) {
    return `<button type="button" class="mx-tile mx-vid${hid}" data-mx-url="${esc(m.url)}" data-mx-video="1"${cap} aria-label="Play video ${i + 1} of ${n}"${hidden ? ' tabindex="-1"' : ''}>`
      + `<video src="${esc(m.url)}#t=0.1" preload="metadata" muted playsinline tabindex="-1" aria-hidden="true"></video>`
      + `<span class="mx-play" aria-hidden="true"></span><span class="mx-badge" aria-hidden="true">Video</span></button>`;
  }
  return `<button type="button" class="mx-tile${hid}" data-mx-url="${esc(m.url)}"${cap} aria-label="Open ${esc(opts.alt || 'photo')} ${i + 1} of ${n}"${hidden ? ' tabindex="-1"' : ''}>`
    + `<img src="${esc(m.url)}" alt="" loading="lazy" decoding="async" draggable="false"></button>`;
}

/** The strip's HTML ('' when there's nothing to show and nothing appended). */
export function mediaStrip(items: Array<string | MediaItem>, opts: StripOpts = {}): string {
  const list: MediaItem[] = (items || []).map(x => (typeof x === 'string' ? { url: x } : x)).filter(x => x && typeof x.url === 'string' && x.url);
  if (!list.length && !opts.append) return '';
  const max = opts.max && opts.max > 0 && list.length > opts.max ? opts.max : list.length;
  const tiles = list.map((m, i) => tile(m, i, list.length, opts, i >= max)).join('');
  const more = max < list.length ? `<button type="button" class="mx-tile mx-more" data-mx-more="${max}" aria-label="Show all ${list.length}">+${list.length - max}</button>` : '';
  return `<div class="mx-strip mx-${opts.size || 'md'}${opts.cls ? ` ${esc(opts.cls)}` : ''}" data-mx-strip data-strip>${tiles}${more}${opts.append || ''}</div>`;
}

/**
 * Merge a record an upload route answered into the page's copy WITHOUT losing
 * media: three uploads finish together and their answers can arrive out of
 * order — an older snapshot must never drop a photo a newer one already showed.
 * Every other field takes the answer's value; media lists (and items' lists)
 * are unions (the answer's order first). Returns `local`.
 */
export function mergeLandedMedia(local: any, fresh: any): any {
  if (!local || typeof local !== 'object') return fresh;
  if (!fresh || typeof fresh !== 'object') return local;
  const LISTS = ['photos', 'videos', 'after_photos', 'prior_after_photos'];
  const METAS = ['photos_meta', 'after_meta'];
  const prev: Record<string, any> = {};
  for (const k of [...LISTS, ...METAS, 'items']) prev[k] = local[k];
  Object.assign(local, fresh);
  const union = (a: any, b: any) => [...new Set([...(Array.isArray(b) ? b : []), ...(Array.isArray(a) ? a : [])])];
  for (const k of LISTS) if (Array.isArray(prev[k]) || Array.isArray(fresh[k])) local[k] = union(prev[k], fresh[k]);
  for (const k of METAS) {
    if (!Array.isArray(prev[k]) && !Array.isArray(fresh[k])) continue;
    const f: any[] = Array.isArray(fresh[k]) ? fresh[k] : [];
    const seen = new Set(f.map((m: any) => m && m.url));
    local[k] = [...f, ...(Array.isArray(prev[k]) ? prev[k] : []).filter((m: any) => m && !seen.has(m.url))];
  }
  if (Array.isArray(fresh.items) && Array.isArray(prev.items)) {
    local.items = fresh.items.map((it: any) => {
      const old = prev.items.find((x: any) => x && it && x.key === it.key);
      return old ? { ...it, photos: union(old.photos, it.photos), ...(Array.isArray(old.videos) || Array.isArray(it.videos) ? { videos: union(old.videos, it.videos) } : {}) } : it;
    });
  }
  return local;
}

// ── queued files (still uploading) ─────────────────────────────────────────
const objUrls = new Map<string, string>();
function objUrlFor(p: PendingItem): string {
  let u = objUrls.get(p.key);
  if (!u) { try { u = URL.createObjectURL(p.blob); objUrls.set(p.key, u); } catch { u = ''; } }
  return u || '';
}
/** Drop the preview urls of files that left the queue. */
export function releasePendingPreviews(liveKeys: Set<string>): void {
  for (const [k, u] of objUrls) if (!liveKeys.has(k)) { try { URL.revokeObjectURL(u); } catch { /* gone */ } objUrls.delete(k); }
}
const pctOf = (p: { sent: number; size: number }) => (p.size > 0 ? Math.max(0, Math.min(100, Math.round((p.sent / p.size) * 100))) : 0);
function pendingLabel(p: PendingItem): string {
  if (p.state === 'waiting') return 'Waiting';
  if (p.state === 'queued') return 'Queued';
  if (!p.sent) return 'Sending…';   // a small photo goes up in one fetch — no byte progress to show
  const pct = pctOf(p);
  return pct >= 100 ? 'Saving…' : `${pct}%`;
}
/** One tile per queued file — a photo shows its own preview, a clip a 🎥 card; both carry a progress bar. */
export function pendingTilesHtml(items: PendingItem[]): string {
  return items.map(p => `<div class="mx-tile mx-pending${p.video ? ' mx-vid' : ''}" data-mx-pending="${esc(p.key)}" role="img" aria-label="${p.video ? 'Video' : 'Photo'} ${esc(p.name)} — ${p.state === 'uploading' ? 'uploading' : p.state} (${esc(fmtBytes(p.size))})" title="${esc(p.name)} · ${esc(fmtBytes(p.size))}">`
    + (p.video ? `<span class="mx-vglyph" aria-hidden="true">🎥<small>${esc(fmtBytes(p.size))}</small></span>` : `<img src="${esc(objUrlFor(p))}" alt="" decoding="async" draggable="false">`)
    + `<span class="mx-prog" aria-hidden="true"><span class="mx-prog-fill${p.state === 'uploading' && !p.sent ? ' mx-ind' : ''}" data-mx-fill style="width:${p.state === 'uploading' ? (p.sent ? pctOf(p) : 35) : 0}%"></span></span>`
    + `<span class="mx-ptxt" data-mx-ptxt>${pendingLabel(p)}</span></div>`).join('');
}
/** Live byte progress for one queued file, without a redraw. */
export function patchPendingProgress(root: ParentNode, key: string, sent: number, total: number): void {
  root.querySelectorAll<HTMLElement>(`[data-mx-pending="${CSS.escape(key)}"]`).forEach(el => {
    const pct = total > 0 ? Math.max(0, Math.min(100, Math.round((sent / total) * 100))) : 0;
    const fill = el.querySelector<HTMLElement>('[data-mx-fill]');
    const txt = el.querySelector<HTMLElement>('[data-mx-ptxt]');
    if (fill) { fill.classList.remove('mx-ind'); fill.style.width = `${pct}%`; }
    if (txt) txt.textContent = pct >= 100 ? 'Saving…' : `${pct}%`;
  });
}

// ── binding (tap → Lightbox, HEVC fallback) ────────────────────────────────
const bound = new WeakSet<EventTarget>();
function markNoVideo(v: HTMLVideoElement): void {
  const t = v.closest<HTMLElement>('.mx-tile');
  if (!t || t.classList.contains('mx-novid')) return;
  t.classList.add('mx-novid');
  t.title = "This browser can't preview this clip — tap to open it";
  v.remove();
  const g = document.createElement('span');
  g.className = 'mx-vglyph';
  g.setAttribute('aria-hidden', 'true');
  g.innerHTML = '🎥<small>Video</small>';
  t.insertBefore(g, t.firstChild);
}
/** Wire every strip under `root` (safe to call after each render; binds once per root). */
export function bindMediaStrips(root: HTMLElement | Document | null | undefined): void {
  if (!root || bound.has(root)) return;
  bound.add(root);
  ensureMediaCss();
  root.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement | null)?.closest?.('[data-mx-url], [data-mx-more]') as HTMLElement | null;
    if (!t || (root instanceof HTMLElement && !root.contains(t))) return;
    const strip = t.closest('[data-mx-strip]');
    if (!strip) return;
    e.preventDefault();
    e.stopPropagation();   // a strip inside a clickable row / card never opens the row too
    const nodes = Array.from(strip.querySelectorAll<HTMLElement>('[data-mx-url]'));
    const urls = nodes.map(n => n.dataset.mxUrl || '');
    const index = t.dataset.mxMore != null ? Number(t.dataset.mxMore) || 0 : Math.max(0, nodes.indexOf(t));
    openLightbox(urls, index, { caption: (i) => nodes[i]?.dataset.mxCap || '' });
  });
  // <video> load / error events don't bubble — capture them at the root.
  root.addEventListener('loadedmetadata', (e) => {
    const v = e.target as HTMLVideoElement;
    if (v && v.tagName === 'VIDEO' && v.closest('.mx-tile') && !v.videoWidth) markNoVideo(v);
  }, true);
  root.addEventListener('error', (e) => {
    const v = e.target as HTMLVideoElement;
    if (v && v.tagName === 'VIDEO' && v.closest('.mx-tile')) markNoVideo(v);
  }, true);
}

const CSS_ID = 'mx-media-css';
const MX_CSS = `
.mx-strip { display:flex; flex-wrap:wrap; gap:6px; align-items:flex-start; }
.mx-tile { position:relative; flex:none; width:var(--mx-s, 72px); height:var(--mx-s, 72px); padding:0; margin:0; border:1px solid var(--border); border-radius:var(--radius-sm, 8px); overflow:hidden; background:var(--bg-subtle); cursor:pointer; display:block; font:inherit; color:inherit; }
.mx-sm .mx-tile { --mx-s:52px; } .mx-md .mx-tile { --mx-s:72px; } .mx-lg .mx-tile { --mx-s:96px; }
.mx-tile:focus-visible { outline:2px solid var(--accent); outline-offset:2px; }
.mx-tile img, .mx-tile video { width:100%; height:100%; object-fit:cover; display:block; pointer-events:none; background:var(--bg-subtle); }
.mx-tile.mx-hide { display:none; }
.mx-vid { background:var(--primary, #0f172a); }
.mx-play { position:absolute; left:50%; top:50%; width:28px; height:28px; margin:-14px 0 0 -14px; border-radius:999px; background:rgba(15,23,42,.62); box-shadow:0 1px 6px rgba(0,0,0,.3); }
.mx-play::after { content:''; position:absolute; left:11px; top:8px; border-style:solid; border-width:6px 0 6px 10px; border-color:transparent transparent transparent #fff; }
.mx-sm .mx-play { width:22px; height:22px; margin:-11px 0 0 -11px; } .mx-sm .mx-play::after { left:8px; top:6px; border-width:5px 0 5px 8px; }
.mx-badge { position:absolute; left:4px; bottom:4px; font-size:9px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:#fff; background:rgba(15,23,42,.62); border-radius:999px; padding:1px 6px; }
.mx-sm .mx-badge { display:none; }
.mx-vglyph { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:3px; font-size:20px; line-height:1; color:#fff; }
.mx-vglyph small { font-size:10px; font-weight:600; opacity:.85; }
.mx-novid { background:var(--primary, #0f172a); }
.mx-novid .mx-play { display:none; }
.mx-more { display:flex; align-items:center; justify-content:center; font-weight:700; font-size:14px; color:var(--text-muted); background:var(--bg-subtle); }
.mx-pending { cursor:default; }
.mx-pending img { opacity:.55; }
.mx-prog { position:absolute; left:6px; right:6px; bottom:6px; height:4px; border-radius:999px; background:rgba(255,255,255,.55); overflow:hidden; }
.mx-prog-fill { display:block; height:100%; background:var(--accent); border-radius:999px; transition:width .2s ease; }
.mx-prog-fill.mx-ind { animation:mx-ind 1.1s ease-in-out infinite alternate; }
@keyframes mx-ind { from { transform:translateX(-10%); } to { transform:translateX(190%); } }
@media (prefers-reduced-motion: reduce) { .mx-prog-fill.mx-ind { animation:none; } }
.mx-ptxt { position:absolute; left:0; right:0; top:4px; text-align:center; font-size:10px; font-weight:700; color:#fff; text-shadow:0 1px 2px rgba(0,0,0,.55); font-variant-numeric:tabular-nums; }
.mx-vid .mx-vglyph { padding-bottom:6px; }
.mx-after .mx-tile:not(.mx-pending):not(.mx-more) { border:2px solid var(--success); }
`;
export function ensureMediaCss(): void {
  if (typeof document === 'undefined' || document.getElementById(CSS_ID)) return;
  const st = document.createElement('style');
  st.id = CSS_ID;
  st.textContent = MX_CSS;
  document.head.appendChild(st);
}
